import React from "react";
import clsx from 'clsx';
import Decimal from "decimal.js";
import { useTranslation } from "react-i18next";
import { useIsMobile } from '../utils/useIsMobile';
import { Tooltip } from '../Tooltip/Tooltip';
import { zIndex } from '../theme/constants';
import { Currency } from "../Currency/Currency";
import { FundInadequacyIcon } from "../FundInadequacyIcon/FundInadequacyIcon";
import { UmbrellaIcon } from "../UmbrellaIcon/UmbrellaIcon";
import { ReactComponent as WalletIcon } from 'common-assets/assets/icons/small-wallet-icon.svg';
import { FlamingoFund, SfdrCategoryId } from "funds-model";
import { renderLeafIcons } from "../SfdrCategoryFundItem/SfdrCategoryFundItem";

import useStyles from "./FundListItem.style";

export interface FundListItemIconsProps {
  fund: FlamingoFund;
  fundValue?: Decimal;
  isFundInadequate: boolean;
  umbrellaColor?: string;
  className?: string;
  fullNameUmbrella?: boolean;
  forceDarkMode?: boolean;
}

const FundListItemIcons: React.FC<FundListItemIconsProps> = ({
  fund,
  fundValue,
  isFundInadequate,
  umbrellaColor,
  className,
  fullNameUmbrella,
  forceDarkMode
}) => {
  const [t] = useTranslation("components");
  const [tt] = useTranslation("purchase-process");
  const isMobile = useIsMobile();
  const umbrellaSize = isMobile ? 24 : 16;
  const { tooltipAppBarZIndex } = zIndex;

  const iconDisplay = {
    wallet: fundValue,
    fundInadequate: isFundInadequate,
    leaf: fund.sfdrCategoryId === SfdrCategoryId.ART_8 || fund.sfdrCategoryId === SfdrCategoryId.ART_9,
    umbrella: fund.umbrella != null,
  };

  const c = useStyles({ forceDarkMode, umbrellaColor, onlyUmbrella: !(iconDisplay.leaf || iconDisplay.fundInadequate || !!iconDisplay.wallet) });

  const renderUmbrellaIcon = () => (
    <div className={c.umbrellaWrapper}>
      {fullNameUmbrella && <span className={c.umbrellaLabelText}>{`${t('fund-list.umbrella')}:`}</span>}
      <UmbrellaIcon
        width={umbrellaSize}
        height={umbrellaSize}
        color={umbrellaColor}
        umbrellaName={fund?.umbrella?.filterLabel}
        tooltipTitleClassName={c.iconTooltipTitle}
      />
      {fullNameUmbrella && <span className={c.umbrellaNameText}>{fund?.umbrella?.filterLabel}</span>}
    </div>
  );

  const renderWalletIcon = () => (
    <Tooltip
      customZIndex={tooltipAppBarZIndex}
      testId="tooltip-walletIcon"
      arrow
      title={
        <div className={c.iconTooltipTitle}>
          {isMobile && <WalletIcon className={c.walletIcon} />}
          <span>
            <span>{`${tt("fund-list.you-have-in-this-fund")} `}</span>
            <Currency
              toFixed={0}
              value={fundValue}
              valueClassName={c.walletValue}
              codeClassName={c.walletValue}
              valueSize='s'
              codeSize='s'
            />
          </span>
        </div>
      }
      customIcon={<WalletIcon className={c.walletIcon} />}
    />
  );

  const renderLeafIcon = () => (
    <Tooltip
      customZIndex={tooltipAppBarZIndex}
      testId="tooltip-leafIcon"
      arrow
      title={
        <div className={c.iconTooltipTitle}>
          {isMobile && (renderLeafIcons(fund.sfdrCategoryId) || <></>)}
          <span>
            {fund.sfdrCategoryId === SfdrCategoryId.ART_8
              ? t('fund-list.sfdr-category-8')
              : t('fund-list.sfdr-category-9')}
          </span>
        </div>
      }
      customIcon={renderLeafIcons(fund.sfdrCategoryId) || <></>}
    />
  );

  const renderInadequacyIcon = () => (
    <FundInadequacyIcon
      variant="small"
      forceDarkMode={forceDarkMode}
      tooltipTitleClassName={c.iconTooltipTitle}
    />
  );

  return (
    <div className={clsx(className, c.iconsWrapper)}>
      {iconDisplay.wallet && renderWalletIcon()}
      {iconDisplay.fundInadequate && renderInadequacyIcon()}
      {iconDisplay.leaf && renderLeafIcon()}
      {iconDisplay.umbrella && renderUmbrellaIcon()}
    </div>
  );
};

export default FundListItemIcons;
